import { getTaxWeekTaxYear } from '@nxsys/utils';
import {
  ContractDetailTypeEnum,
  EeErPensionMethodEnum,
  EmploymentStatusEnum,
  ExpenseTypeEnum,
  HolidayMethodEnum,
  InvoiceStatus,
  LoanTypeEnum,
  MedicalInsuranceTypeEnum,
  PayrollFeeMethodEnum,
  RaiseInvoiceMethodEnum,
  TimesheetStatusEnum,
  VehicleTypeEnum,
  YesNoNAEnum,
} from './AppEnums';

const compare = (a: any, b: any) => {
  if (a.label < b.label || a.title < b.title) {
    return -1;
  }
  if (a.label > b.label || a.title > b.title) {
    return 1;
  }
  return 0;
};

/** 26 traditional counties of the Republic of Ireland (alphabetical). */
export const irelandCountyOptions = [
  { label: 'Carlow', value: 'Carlow' },
  { label: 'Cavan', value: 'Cavan' },
  { label: 'Clare', value: 'Clare' },
  { label: 'Cork', value: 'Cork' },
  { label: 'Donegal', value: 'Donegal' },
  { label: 'Dublin', value: 'Dublin' },
  { label: 'Galway', value: 'Galway' },
  { label: 'Kerry', value: 'Kerry' },
  { label: 'Kildare', value: 'Kildare' },
  { label: 'Kilkenny', value: 'Kilkenny' },
  { label: 'Laois', value: 'Laois' },
  { label: 'Leitrim', value: 'Leitrim' },
  { label: 'Limerick', value: 'Limerick' },
  { label: 'Longford', value: 'Longford' },
  { label: 'Louth', value: 'Louth' },
  { label: 'Mayo', value: 'Mayo' },
  { label: 'Meath', value: 'Meath' },
  { label: 'Monaghan', value: 'Monaghan' },
  { label: 'Offaly', value: 'Offaly' },
  { label: 'Roscommon', value: 'Roscommon' },
  { label: 'Sligo', value: 'Sligo' },
  { label: 'Tipperary', value: 'Tipperary' },
  { label: 'Waterford', value: 'Waterford' },
  { label: 'Westmeath', value: 'Westmeath' },
  { label: 'Wexford', value: 'Wexford' },
  { label: 'Wicklow', value: 'Wicklow' },
];

export const defaultDocumentTypeOptions = [
  { label: 'None', value: 'NONE' },
  {
    label: 'Biometric Residence Permit',
    value: 'BIOMETRIC_RESIDENCE_PERMIT',
  },
  {
    label: 'Residence card',
    value: 'RESIDENCE_CARD',
  },
  { label: 'Passport', value: 'PASSPORT' },
  {
    label: 'Birth Certificate',
    value: 'BIRTH_CERTIFICATE',
  },
  { label: 'Student Visa', value: 'STUDENT_VISA' },
  { label: 'ID Card', value: 'ID_CARD' },
  { label: 'Current Employment', value: 'CURRENT_EMPLOYMENT' },
  { label: 'Previous Employment', value: 'PREVIOUS_EMPLOYMENT' },
  {
    label: 'Agency Questionnaire',
    value: 'AGENCY_QUESTIONNAIRE',
  },
  { label: 'Contract', value: 'CONTRACT' },
  { label: 'Other', value: 'OTHER' },
];

export const defaultDocumentTypeOptionsGroup = [
  { label: 'None', value: 'NONE' },
  { label: 'Other', value: 'OTHER' },
  {
    label: 'Agency Questionnaire',
    value: 'AGENCY_QUESTIONNAIRE',
  },
  { label: 'Contract', value: 'CONTRACT' },
  { label: 'Current Employment', value: 'CURRENT_EMPLOYMENT' },
  { label: 'Previous Employment', value: 'PREVIOUS_EMPLOYMENT' },
  {
    label: 'Identification',
    title: 'Identification',
    options: [
      {
        label: 'Biometric Residence Permit',
        value: 'BIOMETRIC_RESIDENCE_PERMIT',
      },
      {
        label: 'Residence card',
        value: 'RESIDENCE_CARD',
      },
      { label: 'Passport', value: 'PASSPORT' },
      {
        label: 'Birth Certificate',
        value: 'BIRTH_CERTIFICATE',
      },
      { label: 'Student Visa', value: 'STUDENT_VISA' },
      { label: 'ID Card', value: 'ID_CARD' },
    ].sort(compare),
  },
];

export const defaultDocumentsStatus = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Invalid', value: 'INVALID' },
  { label: 'Verified', value: 'VERIFIED' },
].sort(compare);

export const defaultPaymentFrequency = [
  { label: 'Weekly', value: 'WEEKLY' },
  { label: '2 Weekly', value: '2_WEEKLY' },
  { label: '4 Weekly', value: '4_WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
].sort(compare);

export const defaultCisStatus = [
  { label: 'Gross 0%', value: 'GROSS_0%', numberValue: 0, value2: '0%' },
  {
    label: 'Verified 20%',
    value: 'VERIFIED_20%',
    numberValue: 0.2,
    value2: '20%',
  },
  {
    label: 'Un-verified 30%',
    value: 'UN_VERIFIED_30%',
    numberValue: 0.3,
    value2: '30%',
  },
];

export const rctStatusOptions = [
  { label: '0%', value: 'GROSS_0%' },
  { label: '20%', value: 'VERIFIED_20%' },
  { label: '35%', value: 'VERIFIED_35%' },
];

export const holidayPaymentMethodOptions = [
  { label: 'Retained', value: 'RETAINED' },
  { label: 'Advanced', value: 'ROLLED_UP' },
  { label: 'No Holiday Pay', value: 'NO_HOLIDAY_PAY' },
  { label: '52 Week Average', value: 'FIFTY_TWO_WEEK_AVERAGE' },
  {
    label: '8% of worked hours (irregular)',
    value: HolidayMethodEnum.IRREGULAR_HOURS_8_PERCENT,
  },
];

// NX-6284: the 8% irregular-hours method is Ireland-PAYE-only — never offered
// for UK, and never for umbrella (Ireland or UK).
const filterIrregularHolidayMethod = <T extends { value: string }>(
  options: T[],
): T[] =>
  options.filter(
    (o) => o.value !== HolidayMethodEnum.IRREGULAR_HOURS_8_PERCENT,
  );

// 52-week averaging is a UK statutory holiday-pay reference period. A record
// that already has it (saved before a rule closed it off, or imported) keeps
// displaying its value instead of silently blanking out. `current` is the
// record's saved value, not necessarily the live form value.
const filterFiftyTwoWeekAverage = <T extends { value: string }>(
  options: T[],
  current?: string | null,
): T[] =>
  current === HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE
    ? options
    : options.filter(
        (o) => o.value !== HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE,
      );

// Ireland PAYE: No Holiday Pay, Retained, Advanced, and the 8% irregular
// method (NX-6284) — no 52 Week Average, which has no Ireland equivalent
// (NX-3959). UK PAYE: unchanged, every method except the Ireland-only
// irregular one.
export const getHolidayPaymentMethodOptions = (
  isIreland: boolean,
  current?: string | null,
) =>
  isIreland
    ? filterFiftyTwoWeekAverage(holidayPaymentMethodOptions, current)
    : filterIrregularHolidayMethod(holidayPaymentMethodOptions);

// Umbrella never offers 52 Week Average, in any country (pre-dates NX-6284),
// nor the 8% irregular method, which NX-6284 made PAYE-only. A legacy record
// keeps displaying either saved value.
export const getUmbrellaHolidayPaymentMethodOptions = (
  current?: string | null,
) =>
  filterFiftyTwoWeekAverage(
    filterIrregularHolidayMethod(holidayPaymentMethodOptions),
    current,
  );

export const isIrregularHolidayMethod = (method?: string | null): boolean =>
  method === HolidayMethodEnum.IRREGULAR_HOURS_8_PERCENT;

// NX-6284: company-level leave year start, Ireland only (default 1 April).
// Encoded as "day-month" (e.g. "1-4" for 1 April) so the single dropdown can
// carry both halves of the value the API still wants as two numbers.
const LEAVE_YEAR_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// 29 February is left out — a leave year can't start on a date that doesn't
// exist every year — so February always has 28 days here.
const LEAVE_YEAR_DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const toLeaveYearStart = (
  day?: number | string | null,
  month?: number | string | null,
): string | null => {
  const d = Number(day);
  const m = Number(month);
  return d && m ? `${d}-${m}` : null;
};

export const fromLeaveYearStart = (
  value?: string | null,
): { day: number; month: number } | null => {
  if (!value) return null;
  const [day, month] = value.split('-').map(Number);
  return day && month ? { day, month } : null;
};

// Every calendar date from 1 January to 31 December, in calendar order.
export const leaveYearStartDateOptions = LEAVE_YEAR_MONTH_NAMES.flatMap(
  (name, monthIndex) => {
    const month = monthIndex + 1;
    return Array.from(
      { length: LEAVE_YEAR_DAYS_IN_MONTH[monthIndex] },
      (_, i) => {
        const day = i + 1;
        return {
          label: `${day} ${name}`,
          value: toLeaveYearStart(day, month) as string,
        };
      },
    );
  },
);

export const defaultLeaveYearStart = toLeaveYearStart(1, 4) as string;

// Umbrella workers take holiday pay as Advanced or Retained; the 52-week
// average reference period does not apply to umbrella, so the umbrella KID
// form must not offer it (nor pre-fill it from a candidate/company setting).
export const umbrellaHolidayPaymentMethodOptions = filterIrregularHolidayMethod(
  holidayPaymentMethodOptions,
).filter((o) => o.value !== HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE);

export const userTypeOptions = [
  { label: 'Bureau', value: 'bureau' },
  { label: 'Employer', value: 'employer' },
];

export const FPS_VIEW_XML_CONFIG = {
  ITEMS_RENDER: 10,
  ITEMS_RENDER_BEFORE_AFTER: 10,
  ITEM_HEIGHT: 1166,
};

export const defaultRegistrationStatus = [
  { label: 'Ongoing', value: 'ONGOING' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Registration Complete', value: 'REGISTRATION_COMPLETE' },
].sort(compare);

export const defaultMarginMethod = [
  { label: 'Fixed Fee', value: 'FIXED_FEE' },
  { label: 'Fixed Fee Table', value: 'FIXED_FEE_TABLE' },
  { label: '% table on Timesheet Value', value: '%_TABLE_ON_TIMESHEET_VALUE' },
  { label: '% on Timesheet Value', value: '%_ON_TIMESHEET_VALUE' },
  { label: 'None', value: 'NONE' },
].sort(compare);

export const paymentModels = {
  '': 'Not Set',
  CIS: 'CIS',
  NON_CIS: 'Non CIS',
  PAYEE: 'PAYE',
  UMBRELLA: 'Umbrella',
  HYBRID: 'Umbrella Hybrid',
  PAYE_HYBRID: 'PAYE Hybrid',
  PAYE_FTH: 'PAYE FTH',
};

export enum PAYMENT_MODEL_ENUM {
  CIS = 'CIS',
  NON_CIS = 'NON_CIS',
  PAYEE = 'PAYEE',
  UMBRELLA = 'UMBRELLA',
  HYBRID = 'HYBRID',
  PAYE_HYBRID = 'PAYE_HYBRID',
  PAYE_FTH = 'PAYE_FTH',
}

export const paymentModelsOnboarding = {
  '': '_',
  CIS: 'Self Employed - CIS',
  NON_CIS: 'Self Employed - Non CIS',
  PAYEE: 'PAYE',
  UMBRELLA: 'Umbrella',
  HYBRID: 'Hybrid',
  PAYE_HYBRID: 'PAYE Hybrid',
  PAYE_FTH: 'PAYE FTH',
};

export const defaultEmployeeStatusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'LEAVER', label: 'Leavers' },
];

export const pensionProviderIdOptions = [
  { label: 'Smart Pension', value: 'SMART_PENSION' },
  { label: 'NOW Pension', value: 'NOW_PENSIONS' },
  { label: 'NEST Pension', value: 'NEST_PENSION' },
  { label: 'JARVIS Pension', value: 'JARVIS_PENSION' },
  { label: 'Aviva', value: 'AVIVA' },
  { label: 'Aegon', value: 'AEGON' },
  { label: "People's Pension", value: 'PEOPLES_PENSION' },
  { label: 'Royal London', value: 'ROYAL_LONDON' },
  { label: 'Scottish Widows', value: 'SCOTTISH_WIDOWS' },
  { label: 'Standard Life', value: 'STANDARD_LIFE' },
  { label: 'Penfold', value: 'PENFOLD' },
  { label: 'Legal & General', value: 'LEGAL_AND_GENERAL' },
];

export const PensionCalculationTypeEnum = {
  NET_PAY_ARRANGEMENT: 'NET_PAY_ARRANGEMENT',
  RELIEF_AT_SOURCE: 'RELIEF_AT_SOURCE',
};

export const PensionDeductionMethodsEnum = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED: 'FIXED',
};

export const titleOptions = [
  { label: 'Dr', value: 'DR' },
  { label: 'Miss', value: 'MISS' },
  { label: 'Mr', value: 'MR' },
  { label: 'Mrs', value: 'MRS' },
  { label: 'Ms', value: 'MS' },
  { label: 'None', value: 'NONE' },
];

export const genderOptions = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
];

export const maritalStatusOption = [
  { label: 'Single', value: 'SINGLE' },
  { label: 'Married', value: 'MARRIED' },
  { label: 'Divorced', value: 'DIVORCED' },
  { label: 'Prefer Not to Say', value: 'PREFER_NOT_TO_SAY' },
].sort(compare);

export const regStatusOptions = [
  { value: 'ONGOING', label: 'Ongoing' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REGISTRATION_COMPLETE', label: 'Registration Complete' },
  { value: null, label: 'All' },
];

export const regStatusEditOptions = [
  { value: 'ONGOING', label: 'Ongoing' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REGISTRATION_COMPLETE', label: 'Registration Complete' },
];

export const defaultPaymentMethod = [
  { label: 'BACS', value: 'BACS' },
  { label: 'SEPA', value: 'SEPA' },
  { label: 'Faster', value: 'FASTER' },
  { label: 'Cash', value: 'CASH' },
  { label: 'Cheque', value: 'CHEQUE' },
  { label: 'SWIFT', value: 'SWIFT' },
  { label: 'Others', value: 'OTHER' },
].sort(compare);

// NX-5228: Bacs and SEPA are the two ends of the same swap. Bacs is a UK
// bank-clearing scheme with no Irish equivalent; SEPA credit transfer is how a
// euro-area payroll actually pays, and until now it was not offered anywhere.
// So SEPA joins the shared list (the UK can legitimately send euro SEPA
// payments) while Ireland is the only country that loses Bacs.
//
// SWIFT is dropped for Ireland too: an Irish payroll pays by SEPA, which
// already reaches the UK and the rest of the EEA, and no real Irish client on
// prod has a worker set to SWIFT.
//
// Faster Payments is UK-only as well but is kept on request.
const IRELAND_EXCLUDED_PAYMENT_METHODS = ['BACS', 'SWIFT'];

export const getPaymentMethodOptions = (isIreland: boolean) =>
  isIreland
    ? defaultPaymentMethod.filter(
        (o) => !IRELAND_EXCLUDED_PAYMENT_METHODS.includes(o.value),
      )
    : defaultPaymentMethod;

export const regulationOptions = [
  { label: 'Opt-In', value: 'OPT_IN' },
  { label: 'Opt-Out', value: 'OPT_OUT' },
];

export const rightToWorKStatusOptions = [
  { label: 'Appeal', value: 'APPEAL' },
  { label: 'Dependant', value: 'DEPENDANT' },
  { label: 'Indefinite Stay', value: 'INDEFINITE_STAY' },
  { label: 'Refugee', value: 'REFUGEE' },
  { label: 'Tier 1 Visa', value: 'TIER_1_VISA' },
  { label: 'Tier 2 Work Permit', value: 'TIER_2_WORK_PERMIT' },
  { label: 'Tier 4 Student Visa', value: 'TIER_4_STUDENT_VISA' },
  {
    label: 'Tier 5 Temp Workers/Youth Mobility',
    value: 'TIER_5_TEMP_WORKERS/YOUTH_MOBILITY',
  },
  { label: 'Working Holiday', value: 'WORKING_HOLIDAY' },
  { label: 'Pre-Settled', value: 'PRE_SETTLED' },
];

export const NICategoryOptions2023 = [
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'F', value: 'F' },
  { label: 'H', value: 'H' },
  { label: 'I', value: 'I' },
  { label: 'J', value: 'J' },
  { label: 'L', value: 'L' },
  { label: 'M', value: 'M' },
  { label: 'S', value: 'S' },
  { label: 'V', value: 'V' },
  { label: 'Z', value: 'Z' },
  { label: 'X', value: 'X' },
];

export const NICategoryOptions2022 = [
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'J', value: 'J' },
  { label: 'H', value: 'H' },
  { label: 'M', value: 'M' },
  { label: 'Z', value: 'Z' },
  { label: 'X', value: 'X' },
];

export const freeportNIcategories = [
  { label: 'F', value: 'F' },
  { label: 'I', value: 'I' },
  { label: 'L', value: 'L' },
  { label: 'N', value: 'N' },
  { label: 'S', value: 'S' },
  { label: 'E', value: 'E' },
  { label: 'D', value: 'D' },
  { label: 'K', value: 'K' },
];

export const NICategoryOptions = (() => {
  const { taxYear } = getTaxWeekTaxYear();
  switch (taxYear) {
    case 2025:
      return [...NICategoryOptions2023, ...freeportNIcategories];
    case 2023:
      return NICategoryOptions2023;
    default:
      return NICategoryOptions2022;
  }
})();

export const starterTypeOptionsForIreland = [
  { label: 'New employee', value: 'NEW_WITHOUT_P45' },
  { label: 'Existing employee', value: 'EXISTING' },
];

export const starterDeclarationOptions = [
  {
    label:
      'This is their first job since the start of the tax year (6th April)',
    value: 'FIRST_JOB_SINCE_START_TAX_YEAR',
  },
  { label: 'This is currently their only job', value: 'CURRENTLY_ONLY_JOB' },
  { label: 'They have another job or pension', value: 'HAVE_ANOTHER_JOB' },
];

export const starterTypeOptions = [
  { label: 'New employee with P45', value: 'NEW_WITH_P45' },
  { label: 'New employee without P45', value: 'NEW_WITHOUT_P45' },
  { label: 'Existing employee', value: 'EXISTING' },
];

export const holidayPaymentMethodOnboardingOptions = [
  { label: 'Retained', value: 'RETAINED' },
  { label: 'Advanced', value: 'ROLLED_UP' },
  { label: '52 Week Average', value: 'FIFTY_TWO_WEEK_AVERAGE' },
  { label: 'No Holiday Pay', value: 'NO_HOLIDAY_PAY' },
  {
    label: '8% of worked hours (irregular)',
    value: HolidayMethodEnum.IRREGULAR_HOURS_8_PERCENT,
  },
];

// NX-5228/NX-6284: this onboarding step sets the worker's PAYE holidayPayMethod,
// so it follows the same rule as getHolidayPaymentMethodOptions — Ireland
// excludes only 52 Week Average, UK excludes only the Ireland-only irregular
// method. Separate list because onboarding has its own label set.
export const getHolidayPaymentMethodOnboardingOptions = (
  isIreland: boolean,
  current?: string | null,
) =>
  isIreland
    ? filterFiftyTwoWeekAverage(holidayPaymentMethodOnboardingOptions, current)
    : filterIrregularHolidayMethod(holidayPaymentMethodOnboardingOptions);

export const optStatePensionOptions = [
  {
    label: 'Ignition',
    value: 'ignition',
  },
  {
    label: 'Opted-In',
    value: 'opted_in',
  },
  {
    label: 'Opted-Out',
    value: 'opted_out',
  },
  {
    label: 'Rejoined',
    value: 'rejoined',
  },
  {
    label: 'Ceased Membership',
    value: 'ceased_membership',
  },
];

export const optStateNowPensionOptions = [
  {
    label: 'Opted-In',
    value: 'opted_in',
  },
  {
    label: 'Opted-Out',
    value: 'opted_out',
  },
  {
    label: 'Rejoined',
    value: 'rejoined',
  },
];

export const RTW_EXPIRY_WARNING_DAYS = 30;

export const defaultRightToWorkStatus = [
  { label: 'Appeal', value: 'APPEAL' },
  { label: 'Dependant', value: 'DEPENDANT' },
  { label: 'Indefinite Stay', value: 'INDEFINITE_STAY' },
  { label: 'Refugee', value: 'REFUGEE' },
  { label: 'Tier 1 Visa', value: 'TIER_1_VISA' },
  { label: 'Tier 2 Work Permit', value: 'TIER_2_WORK_PERMIT' },
  { label: 'Tier 4 Student Visa', value: 'TIER_4_STUDENT_VISA' },
  {
    label: 'Tier 5 Temp Workers/Youth Mobility',
    value: 'TIER_5_TEMP_WORKERS/YOUTH_MOBILITY',
  },
  { label: 'Working Holiday', value: 'WORKING_HOLIDAY' },
].sort(compare);

export const USCRateList = {
  USE1: [
    {
      uscRatePercent: 0.5,
      key: 'cutOffPointsZeroPointFive',
      label: '0.5% Cut off points',
    },
    { uscRatePercent: 2, key: 'cutOffPointsTwo', label: '2% Cut off points' },
  ],
  USE2: [
    {
      uscRatePercent: 0.5,
      key: 'cutOffPointsZeroPointFive',
      label: '0.5% Cut off points',
    },
    { uscRatePercent: 2, key: 'cutOffPointsTwo', label: '2% Cut off points' },
    {
      uscRatePercent: 3,
      key: 'cutOffPointsFourPointFive',
      label: '3% Cut off points',
    },
  ],
  USE3: [
    {
      uscRatePercent: 0.5,
      key: 'cutOffPointsZeroPointFive',
      label: '0.5% Cut off points',
    },
    { uscRatePercent: 2, key: 'cutOffPointsTwo', label: '2% Cut off points' },
    {
      uscRatePercent: 3,
      key: 'cutOffPointsFourPointFive',
      label: '3% Cut off points',
    },
    { uscRatePercent: 8, key: 'cutOffPointsEight', label: '8% Cut off points' },
  ],
  STANDARD: [
    {
      uscRatePercent: 0.5,
      key: 'cutOffPointsZeroPointFive',
      label: '0.5% Cut off points',
    },
    { uscRatePercent: 2, key: 'cutOffPointsTwo', label: '2% Cut off points' },
    {
      uscRatePercent: 3,
      key: 'cutOffPointsFourPointFive',
      label: '3% Cut off points',
    },
    { uscRatePercent: 8, key: 'cutOffPointsEight', label: '8% Cut off points' },
  ],
};

export const TaxExclusionOptions = [
  { value: 'NO_EXCLUSION_ORDER', label: 'No exclusion order' },
  {
    value: 'EXCLUSION_ORDER_WITHOUT_A1',
    label: 'Exclusion order (without A1)',
  },
  { value: 'EXCLUSION_ORDER_WITH_A1', label: 'Exclusion order (with A1)' },
];

export const ASCCalculationMethodOptions = [
  { value: 'NOT_APPLICABLE', label: 'Not applicable' },
  { value: 'STANDARD_ACCRUAL', label: 'Standard Accrual Pension Scheme' },
  { value: 'FACT_ACCRUAL', label: 'Fast Accrual Pension Scheme' },
  { value: 'SINGLE', label: 'Single Pension Scheme' },
];

export const PRSIExemptionOptions = [
  { value: 'NOT_EXEMPT', label: 'Not Exempt from PRSI' },
  {
    value: 'A1_PORTABLE_DOCUMENT',
    label: 'A1 Portable Document from EU Member State',
  },
  {
    value: 'CERTIFICATE_OF_COVERAGE',
    label: 'Certificate of coverage under Social Security Bilateral Agreement',
  },
  {
    value: 'OTHER_NOTIFICATION_OF_EXEMPTION',
    label: 'Other notification of exemption for posted workers',
  },
  {
    value: 'EMPLOYMENT_FAMILY_MEMBER',
    label: 'Employment of certain family members',
  },
  { value: 'UNDER_16_YEARS_OF_AGE', label: 'Under 16 years of age' },
  {
    value: 'EMPLOYMENT_SOCIAL_WELFARE',
    label: 'Employment on certain social welfare schemes',
  },
  { value: 'EXEMPT_FOR_OTHER_REASON', label: 'Exempt for other reason' },
];

export const uscExemptionOptions = [
  { value: 'NOT_EXEMPT', label: 'Not Exempt from USC' },
  { value: 'EXEMPT', label: 'Exempt from USC' },
];

export const USCTiers = [
  { value: 'STANDARD', label: 'Apply standard USC cut off point values' },
  { value: 'USE1', label: 'Use 1 cut off point' },
  { value: 'USE2', label: 'Use 2 cut off point' },
  { value: 'USE3', label: 'Use 3 cut off point' },
];

export const defaultPaymentTerm = [
  { label: 'On invoicing', value: 'ON_INVOICING' },
  { label: 'Full Cash Receipt', value: 'FULL_CASH_RECEIPT' },
  { label: 'Proportion to Cash receipt', value: 'PROPORTION_TO_CASH_RECEIPT' },
].sort(compare);

export const defaultsBusinessTypeOptions = [
  { label: 'Sole Trader', value: 'SOLE_TRADER' },
  { label: 'Company', value: 'COMPANY' },
  { label: 'Partnership', value: 'PARTNERSHIP' },
];

export const defaultPartnerCategoryOptions = [
  { label: 'Individual', value: 'INDIVIDUAL' },
  { label: 'Company', value: 'COMPANY' },
];

export const expenseClaimRuleOptions = [
  {
    label: 'Disallow',
    value: 'DISALLOW',
  },
  {
    label: 'Allow',
    value: 'ALLOW',
  },
];

export const KIDEmailType = {
  SEND_KID: 'KID',
  SEND_KID_PAY_ILLUSTRATION: 'Pay Illustration',
  SEND_KID_PAYE: 'KID',
  SEND_KID_PAYE_PAY_ILLUSTRATION: 'Pay Illustration',
  SEND_KID_UMBRELLA: 'KID',
  SEND_KID_UMBRELLA_PAY_ILLUSTRATION: 'Pay Illustration',
};

export const KIDEmailTypeEnum = {
  SEND_KID: 'KID',
  SEND_KID_PAY_ILLUSTRATION: 'PAY_ILLUSTRATION',
  SEND_KID_PAYE: 'KID',
  SEND_KID_PAYE_PAY_ILLUSTRATION: 'PAY_ILLUSTRATION',
  SEND_KID_UMBRELLA: 'KID',
  SEND_KID_UMBRELLA_PAY_ILLUSTRATION: 'PAY_ILLUSTRATION',
};

export const KIDPaymentModel = {
  SEND_KID: 'CIS/Non CIS',
  SEND_KID_PAY_ILLUSTRATION: 'CIS/Non CIS',
  SEND_KID_PAYE: 'PAYE',
  SEND_KID_PAYE_PAY_ILLUSTRATION: 'PAYE',
  SEND_KID_UMBRELLA: 'Umbrella',
  SEND_KID_UMBRELLA_PAY_ILLUSTRATION: 'Umbrella',
};
export const defaultPaymentRateTypeOptions = [
  // { label: 'Fixed', value: 'FIXED' },
  // { label: 'Variable', value: 'VARIABLE' },
  // { label: 'Payment Only', value: 'PAYMENT_ONLY' },
  { label: 'Cost of Materials', value: 'COST_OF_MATERIALS' },
  { label: 'Rate', value: 'RATE' },
].sort(compare);

export const defaultUnits = [
  { label: 'Daily', value: 'DAILY' },
  { label: 'Hourly', value: 'HOURLY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
].sort(compare);

export const defaultInvoiceStatusOptions = [
  { label: 'All', value: InvoiceStatus.ALL },
  { label: 'Outstanding', value: InvoiceStatus.OUTSTANDING },
  { label: 'Paid', value: InvoiceStatus.PAID },
];

export const defaultInvoiceRaisedAfterOptions = [
  { label: 'After confirmed payment', value: 'AFTER_CONFIRMED_PAYMENT' },
  { label: 'After draft payroll', value: 'AFTER_DRAFT_PAYROLL' },
];

export const defaultInvoiceRaisedMethodOptions = [
  { label: 'Raise difference only', value: 'RAISE_DIFFERENCE_ONLY' },
  { label: 'Raise all employment costs', value: 'RAISE_ALL_EMPLOYMENT_COSTS' },
];

export const defaultRuleInvoicingMethod = [
  { label: 'Of the following month', value: 'OF_THE_FOLLOWING_MONTH' },
  {
    label: 'Day(s) after the invoice date',
    value: 'DAYS_AFTER_THE_INVOICE_DATE',
  },
  { label: 'Of the current month', value: 'OF_THE_CURRENT_MONTH' },
];

export const defaultPayrollFreeMethod = [
  {
    label: 'Fixed Fee per employee',
    value: PayrollFeeMethodEnum.FIXED_FEE_PER_EMPLOYEE,
  },
  { label: 'Tiered fee', value: PayrollFeeMethodEnum.TIERED_FEE },
];

export const defaultInvoiceTemplateOptions = [
  { label: 'Default template', value: 'DEFAULT' },
  { label: 'Breakdown per employee template', value: 'BREAKDOWN_PER_EMPLOYEE' },
];

export const invoicePaymentModelOptions = [
  { label: 'PAYE', value: PAYMENT_MODEL_ENUM.PAYEE },
  { label: 'RCT', value: PAYMENT_MODEL_ENUM.CIS },
  { label: 'All', value: 'ALL' },
];

export const invoiceMethodOptions = [
  { label: 'Detailed', value: RaiseInvoiceMethodEnum.DETAILED },
  { label: 'Summary', value: RaiseInvoiceMethodEnum.SUMMARY },
];

export const timesheetStatusOptions = [
  { label: 'Awaiting Approval', value: TimesheetStatusEnum.AWAITING_APPROVAL },
  { label: 'Rejected', value: TimesheetStatusEnum.REJECTED },
  { label: 'Approved', value: TimesheetStatusEnum.APPROVED },
];

export const paymentModelOptions = [
  {
    label: 'CIS',
    value: PAYMENT_MODEL_ENUM.CIS,
  },
  {
    label: 'NON CIS',
    value: PAYMENT_MODEL_ENUM.NON_CIS,
  },
  {
    label: 'PAYE',
    value: PAYMENT_MODEL_ENUM.PAYEE,
  },
  {
    label: 'Umbrella',
    value: PAYMENT_MODEL_ENUM.UMBRELLA,
  },
  {
    label: 'Umbrella Hybrid',
    value: PAYMENT_MODEL_ENUM.HYBRID,
  },
];

export const paymentModelOptionsIreland = [
  {
    label: 'RCT',
    value: PAYMENT_MODEL_ENUM.CIS,
  },
  {
    label: 'PAYE',
    value: PAYMENT_MODEL_ENUM.PAYEE,
  },
  {
    label: 'Umbrella',
    value: PAYMENT_MODEL_ENUM.UMBRELLA,
  },
];

const findIrelandPaymentModelLabel = (value: string) =>
  paymentModelOptionsIreland.find((opt) => opt.value === value)?.label;

/**
 * Selectable dropdown options for an Ireland payment-model picker.
 * `isBureau` drops UMBRELLA — Nxsys Ireland (bureau) offers PAYE/RCT only,
 * the Umbrella payment model exists only on the umbrella product.
 */
export const getIrelandPaymentModelOptions = (isBureau: boolean) =>
  isBureau
    ? paymentModelOptionsIreland.filter(
        (opt) => opt.value !== PAYMENT_MODEL_ENUM.UMBRELLA,
      )
    : paymentModelOptionsIreland;

/** Query-filter payment-model list for an Ireland tenant (not a dropdown). */
export const UMBRELLA_IRELAND_PAYMENT_MODELS = [
  PAYMENT_MODEL_ENUM.PAYEE,
  PAYMENT_MODEL_ENUM.CIS,
  PAYMENT_MODEL_ENUM.UMBRELLA,
];
/**
 * Whether an employee gets the Benefit in Kind tab.
 *
 * BIK is an Irish concept here — the UK side records benefits through payrolled
 * BIK on the RTI path, not this tab. Within Ireland it used to be limited to
 * PAYEE, which silently excluded every umbrella employee even though the
 * calculation supports the benefit: an umbrella-Ireland worker carries
 * paymentModel UMBRELLA, so the tab was simply unreachable for them.
 *
 * A named predicate rather than an inline condition because country and
 * payment model are orthogonal, and conflating the two is exactly the mistake
 * this area keeps making.
 */
export const canUseBenefitInKindTab = (
  isIreland: boolean,
  paymentModel?: string | null,
) =>
  isIreland &&
  (paymentModel === PAYMENT_MODEL_ENUM.PAYEE ||
    paymentModel === PAYMENT_MODEL_ENUM.UMBRELLA);

export const getIrelandPaymentModels = (isBureau: boolean) =>
  isBureau
    ? [PAYMENT_MODEL_ENUM.PAYEE, PAYMENT_MODEL_ENUM.CIS]
    : UMBRELLA_IRELAND_PAYMENT_MODELS;

// Ireland stores RCT as the BE value CIS, so it must not fall back to the UK
// labels. Use these wherever a stored payment model is rendered — the two
// differ only in the non-Ireland label set they fall back to.
export const getPaymentModelLabel = (
  value?: string | null,
  isIreland = false,
): string => {
  if (!value) return '';
  if (isIreland) return findIrelandPaymentModelLabel(value) ?? value;
  return paymentModels[value as keyof typeof paymentModels] ?? value;
};

export const getPaymentModelOnboardingLabel = (
  value?: string | null,
  isIreland = false,
): string => {
  if (!value) return '';
  if (isIreland) return findIrelandPaymentModelLabel(value) ?? value;
  return (
    paymentModelsOnboarding[value as keyof typeof paymentModelsOnboarding] ??
    value
  );
};

export const defaultStatusOptions = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

export const defaultCurrentCyOption = [
  { label: 'GBP', value: 'GBP' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
].sort(compare);

export const yesNoOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export const yesNoNAOptions = [
  { label: 'Yes', value: YesNoNAEnum.YES },
  { label: 'No', value: YesNoNAEnum.NO },
  { label: 'N/A', value: YesNoNAEnum.NOT_AVAILABLE },
];

export const defaultBank = [
  { value: 'HSBC', label: 'HSBC' },
  { value: 'Natwest', label: 'Natwest' },
  { value: 'RBS', label: 'RBS' },
  { value: 'Lloyds', label: 'Lloyds' },
  { value: 'Santander', label: 'Santander' },
  { value: 'Metro', label: 'Metro' },
  { value: 'Barclays', label: 'Barclays' },
  { value: 'Revolut', label: 'Revolut' },
  { value: 'Monzo', label: 'Monzo' },
  { value: 'Trilogy', label: 'Trilogy' },
  { value: 'Modulr', label: 'Modulr' },
  { value: 'Lloyds Faster Payment', label: 'Lloyds Faster Payment' },
  { value: 'Serxa', label: 'Serxa' },
  { value: 'BACS 18 (No Header)', label: 'BACS 18 (No Header)' },
  { value: 'HSBC BACS 18', label: 'HSBC BACS 18' },
  { value: 'Danske', label: 'Danske' },
  { value: 'Virgin Money', label: 'Virgin Money' },
  { value: 'Finastra', label: 'Finastra' },
  { value: 'TBacs', label: 'TBacs' },
  { value: 'FX Masters', label: 'FX Masters' },
  { value: 'Natwest Ad Hoc Bulk', label: 'Natwest Ad Hoc Bulk' },
].sort(compare);

export const pensionDeductionMethods = [
  {
    label: 'Percentage (%)',
    value: 'PERCENTAGE',
  },
  {
    label: 'Fixed',
    value: 'FIXED',
  },
];

export const reasonOptions = [
  { label: 'Leaver', value: 'LEAVER' },
  { label: 'Retired', value: 'RETIRED' },
  { label: 'Deceased', value: 'DECEASED' },
];

export const PensionProvidersName = {
  NOW_PENSIONS: 'Now Pension',
  SMART_PENSION: 'Smart Pension',
  NEST_PENSION: 'Nest Pension',
  JARVIS_PENSION: 'Jarvis Pension',
  AVIVA: 'Aviva',
  AEGON: 'Aegon',
  PEOPLES_PENSION: "People's Pension",
  ROYAL_LONDON: 'Royal London',
  SCOTTISH_WIDOWS: 'Scottish Widows',
  STANDARD_LIFE: 'Standard Life',
  PENFOLD: 'Penfold',
  LEGAL_AND_GENERAL: 'Legal & General',
};

export const defaultDateLeftReason = [
  { label: 'End of Assignment', value: 'END_OF_ASSIGNMENT' },
  { label: 'Left Country', value: 'LEFT_COUNTRY' },
  { label: 'Deceased', value: 'DECEASED' },
  { label: 'Dormant', value: 'DORMANT' },
  { label: 'Terminated', value: 'TERMINATED' },
  { label: 'Other', value: 'OTHER' },
  { label: 'Leaver', value: 'LEAVER' },
  { label: 'Retired', value: 'RETIRED' },
].sort(compare);

export const defaultTaxBasisOptions = [
  { label: 'Cumulative', value: 'CUMULATIVE' },
  { label: 'W1M1', value: 'W1M1' },
];

export const defaultDeductionTypeOptions = [
  { label: 'Fixed', value: 'FIXED' },
  { label: 'Variable', value: 'VARIABLE' },
].sort(compare);

export const dataAttachmentOrder = [
  {
    key: '1',
    code: 'A005',
    des: 'Attachment of Earnings Order (Northern Ireland)',
    valueDes: 'ATTACHMENT_OF_EARNINGS_AEO',
    authority: `Magistrates' Courts in E&W`,
    type: 'Fixed Amount',
    disabled: false,
  },
  {
    key: '2',
    code: 'A006',
    des: 'CCAEO (Community Charge) Before 1 Apr 1992 [Table]',
    valueDes: 'CCAEO_COMMUNITY_CHARGE_BEFORE_1_APR_1992_TABLE',
    authority: `Local Authorities in E&W`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '3',
    code: 'A007',
    des: 'CCAEO (Community Charge) From 1 Apr 1992 [Table]',
    valueDes: 'CCAEO_COMMUNITY_CHARGE_FROM_1_APR_1992_TABLE',
    authority: `Local Authorities in E&W`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '4',
    code: 'A008',
    des: 'CTAEO (Council Tax) Before 1 Oct 1998 [Table]',
    valueDes: 'CTAEO_COUNCIL_TAX_BEFORE_1_OCT_1998_TABLE',
    authority: `Local Authorities in E&W`,
    type: 'Table',
    disabled: false,
  },

  {
    key: '5',
    code: 'A009',
    des: 'Attachment of Earnings Order - Fines',
    valueDes: 'CTAEO_COUNCIL_TAX_FROM_1_OCT_98_TO_MAR_07_TABLE',
    authority: `Local Authorities in E&W`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '6',
    code: 'A011',
    des: 'Council Tax Attachment of Earnings Order (CTAEO)',
    valueDes: 'CTAEO_COUNCIL_TAX_FROM_1_APR_2007_TABLE',
    authority: `Local Authorities in E&W`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '7',
    code: 'A013',
    des: 'Earnings Arrestment [Tables] - Refer A027 for 2010',
    valueDes: 'EARNINGS ARRESTMENT_TABLES_REFER_A027_FOR_2010',
    authority: `Sheriff Court in Scotland`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '8',
    code: 'A016',
    des: 'Conjoined Arrestment Order (CAO) [Table]',
    valueDes: 'CONJOINED_ARRESTMENT ORDER_CAO_TABLE',
    authority: `Sheriff Court in Scotland`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '9',
    code: 'A028',
    des: 'Direct Earnings Attachment (Standard Rate)',
    valueDes: 'DIRECT_EARNINGS_ATTACHMENT_STANDARD_DATE',
    authority: `Department for Work & Pensions`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '10',
    code: 'A030',
    des: 'Earnings Arrestment (Scotland)',
    valueDes: 'EARNINGS_ARRESTMENT_TABLE_2014',
    authority: `Sheriff Court in Scotland`,
    type: 'Table',
    disabled: false,
  },
  {
    key: '11',
    code: 'A031',
    des: 'Direct Earnings Attachment (Higher Rate)',
    valueDes: 'DIRECT_EARNINGS_ATTACHMENT_HIGHER_RATE',
    authority: 'Department for Work & Pensions',
    type: 'Table',
    disabled: false,
  },
  {
    key: '12',
    code: 'DEO',
    des: 'Child Support Deduction from Earnings (DEO)',
    valueDes: 'CHILD_SUPPORT_DEDUCTION_FROM_EARNINGS',
    authority: 'Department for Work & Pensions',
    type: 'Fixed Amount',
    disabled: false,
  },
];

export const defaultAttachmentOderDesOptions = dataAttachmentOrder
  .filter((item) =>
    ['A005', 'A011', 'A028', 'A030', 'A031', 'DEO', 'A009'].includes(item.code),
  )
  .map((item) => ({
    label: item.des,
    value: item.valueDes,
    type: item.type,
    disabled: item.disabled,
    code: { label: item.code, value: item.code, disabled: item.disabled },
  }));

// NX-5642: Total Attachment is optional for every order type. An empty value
// is sent as null (= no cap) so deductions continue until the order ends or is
// switched off.
export const toTotalAttachmentPayload = (
  value: string | number | null | undefined,
): number | null => (value !== '' && value != null ? Number(value) : null);

export const defaultSubcontractorStatusOption = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
].sort(compare);

export const categoryPensionOptions = [
  {
    label: 'Eligible Jobholder',
    value: 'Eligible Jobholder',
  },
  {
    label: 'Non-Eligible Jobholder',
    value: 'Non-Eligible Jobholder',
  },
  {
    label: 'Entitled Worker',
    value: 'Entitled Worker',
  },
];

export const optStateNestPensionOptions = [
  {
    label: 'Opted-In',
    value: 'opted_in',
  },
  {
    label: 'Opted-Out',
    value: 'opted_out',
  },
];

export const expenseTypes = {
  other: 'Other',
  openingBalance: 'Opening Balance',
};

export const expenseMilesOptions = [
  {
    label: 'Car/Van',
    value: 'carOrVan',
  },
  {
    label: 'Motorcycle',
    value: 'motorcycle',
  },
  {
    label: 'Electric Car',
    value: 'electricCar',
  },
];

export const expenseTypeOptions = [
  {
    label: 'Mileage',
    value: ExpenseTypeEnum.MILEAGE,
  },
  {
    label: 'Others',
    value: ExpenseTypeEnum.OTHER,
  },
];

export const defaultMessagePriorityOptions = [
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
];

export const defaultMessageStatusOptions = [
  { label: 'New', value: 'NEW' },
  { label: 'WIP', value: 'WIP' },
  { label: 'Closed', value: 'CLOSED' },
];

export const defaultEmploymentStatusOptions = [
  {
    label: 'Employee',
    value: EmploymentStatusEnum.EMPLOYEE,
    description:
      'Hired with a contract of employment, with a full range of rights and job security',
  },
  {
    label: 'Director',
    value: EmploymentStatusEnum.DIRECTOR,
    description:
      'Appointed to a company board and are responsible for complying with relevant laws',
  },
  {
    label: 'Worker',
    value: EmploymentStatusEnum.WORKER,
    description:
      'Performs tasks or services for a business and has basic rights, but isn’t classed as an employee',
  },
  {
    label: 'Off-payroll worker',
    value: EmploymentStatusEnum.OFF_PAYROLL,
    description:
      'Provides services through their personal company, rather than being employed by your business',
  },
];

export const defaultContractTypeOptions = [
  {
    label: 'Full-time',
    value: 'FULL_TIME',
    description:
      'Working a standard number of hours each week (often around 35-40 hours) with a full range of benefits',
  },
  {
    label: 'Part-time',
    value: 'PART_TIME',
    description:
      'Working fewer hours than full-time (often less than 30-35 hours), with proportional benefits',
  },
  {
    label: 'Fix-term contract',
    value: 'FIX_TERM_CONTRACT',
    description:
      'Hired for a specific period or project, with similar rights to permanent employees during the term',
  },
  {
    label: 'Apprentice',
    value: 'APPRENTICE',
    description:
      'Employed to learn a trade or skill, while training through an approved learning provider',
  },
  {
    label: 'Zero-hour contract',
    value: 'ZERO_HOUR_CONTRACT',
    description:
      'Hired for short-term roles or seasonal work, with defined rights and protections for the duration of their employment',
  },
];

export const defaultContractDetailTypeOptions = [
  {
    label: 'New employee',
    value: ContractDetailTypeEnum.NEW_EMPLOYEE,
    description: 'Setting up a new starter with this organization',
  },
  {
    label: 'Existing employee',
    value: ContractDetailTypeEnum.EXISTING_EMPLOYEE,
    description: 'Migrating paid employee from previous payroll software',
  },
];

export const defaultEmploymentBasisOptions = [
  {
    label: 'Full-time',
    value: 'FULL_TIME',
  },
  {
    label: 'Part-time',
    value: 'PART_TIME',
  },
  {
    label: 'Fix-term contract',
    value: 'FIX_TERM_CONTRACT',
  },
  {
    label: 'Apprentice',
    value: 'APPRENTICE',
  },
  {
    label: 'Zero-hour contract',
    value: 'ZERO_HOUR_CONTRACT',
  },
];

export const defaultVehicleTypeOptions = [
  {
    label: 'Car',
    value: VehicleTypeEnum.CAR,
  },
  {
    label: 'Van',
    value: VehicleTypeEnum.VAN,
  },
];

export const defaultCO2EmissionsOptions = [
  { label: 'Ogm/km - 59gm/km', value: '0_TO_59' },
  { label: '59gm/km - 99gm/km', value: 'OVER_59_TO_99' },
  { label: '99gm/km - 139gm/km', value: 'OVER_99_TO_139' },
  { label: '139gm/km - 179gm/km', value: 'OVER_139_TO_179' },
  { label: 'Over 179gm/km', value: 'OVER_179' },
];

export const defaultLoanTypesOptions = [
  { label: 'Qualifying home loan', value: LoanTypeEnum.QUALIFYING_HOME_LOAN },
  { label: 'Other loan', value: LoanTypeEnum.OTHER_LOAN },
];

export const defaultAccommodationTypesOptions = [
  { label: 'Property value', value: 'PROPERTY_VALUE' },
  { label: 'Monthly Rent', value: 'MONTHLY_RENT' },
];

export const defaultMedicalInsuranceTypesOptions = [
  { label: 'Annual', value: MedicalInsuranceTypeEnum.ANNUAL },
  { label: 'One-off', value: MedicalInsuranceTypeEnum.ONE_OFF },
];

export const employmentStatusOptions = [
  {
    value: 'THE_EMPLOYEES_FIRST_OR_ONLY_EMPLOYMENT',
    label:
      "This is the employee's first or only employment with you in the tax year.",
  },
  {
    value: 'AN_ADDITIONAL_EMPLOYMENT_OR_RECOMMENCEMENT',
    label:
      'This is an additional employment or recommencement in the tax year.',
  },
  {
    value: 'MANUALLY_THE_EMPLOYMENT_ID',
    label: "I want to manually enter the 'Employment ID'",
  },
];

export const defaultTaxBasisIrelandOptions = [
  { label: 'Cumulative', value: 'CUMULATIVE' },
  { label: 'W1M1', value: 'W1M1' },
  { label: 'Emergency', value: 'EMERGENCY' },
];

export const defaultTaxExclusionOptions = [
  { value: 'NO_EXCLUSION_ORDER', label: 'No exclusion order' },
  {
    value: 'EXCLUSION_ORDER_WITHOUT_A1',
    label: 'Exclusion order (without A1)',
  },
  { value: 'EXCLUSION_ORDER_WITH_A1', label: 'Exclusion order (with A1)' },
];

export const defaultUSCExemptionOptions = [
  { value: 'NOT_EXEMPT', label: 'Not Exempt from USC' },
  { value: 'EXEMPT', label: 'Exempt from USC' },
];

export const defaultUSCTiersOptions = [
  { value: 'STANDARD', label: 'Apply standard USC cut off point values' },
  { value: 'USE1', label: 'Use 1 cut off point' },
  { value: 'USE2', label: 'Use 2 cut off point' },
  { value: 'USE3', label: 'Use 3 cut off point' },
];

export const defaultPRSIEeClassesOptions = [
  {
    value: 'A0_A1',
    label: 'Class J0/A0/AX/AL/A1',
    desc: 'Employee aged 16 to 65',
  },
  {
    value: 'A8_A9',
    label: 'Class A8/A9',
    desc: 'Community Employment participant',
  },
  {
    value: 'J_16_65',
    label: 'Class J0/J1',
    desc: 'Employee aged 16 to 65 in subsidiary employment',
  },
  {
    value: 'J_OVER_65',
    label: 'Class J0/J1',
    desc: 'Employee aged over 65',
  },
  {
    value: 'M_UNDER_15',
    label: 'Class M',
    desc: 'Employee aged 15 or under',
  },
  {
    value: 'M_PENSION',
    label: 'Class M',
    desc: 'Employee in receipt of occupational pension',
  },
  {
    value: 'M_K1',
    label: 'Class M/K1',
    desc: 'Office holder',
  },
  {
    value: 'B',
    label: 'Class B',
    desc: 'Public sector employee',
  },
  {
    value: 'C',
    label: 'Class C',
    desc: 'Public sector employee',
  },
  {
    value: 'D',
    label: 'Class D',
    desc: 'Public sector employee',
  },
  {
    value: 'H',
    label: 'Class H',
    desc: 'Public sector employee',
  },
  {
    value: 'E',
    label: 'Class E',
    desc: 'Minister of religion employed by COI Representative Body',
  },
  {
    value: 'S',
    label: 'Class S',
    desc: 'Self-employed qualifying director',
  },
  {
    value: 'M_OVER_66',
    label: 'Class M',
    desc: 'Self-employed qualifying director aged 66 or over',
  },
];

export const defaultPRSIExemptionOptions = [
  { value: 'NOT_EXEMPT', label: 'Not Exempt from PRSI' },
  {
    value: 'A1_PORTABLE_DOCUMENT',
    label: 'A1 Portable Document from EU Member State',
  },
  {
    value: 'CERTIFICATE_OF_COVERAGE',
    label: 'Certificate of coverage under Social Security Bilateral Agreement',
  },
  {
    value: 'OTHER_NOTIFICATION_OF_EXEMPTION',
    label: 'Other notification of exemption for posted workers',
  },
  {
    value: 'EMPLOYMENT_FAMILY_MEMBER',
    label: 'Employment of certain family members',
  },
  { value: 'UNDER_16_YEARS_OF_AGE', label: 'Under 16 years of age' },
  {
    value: 'EMPLOYMENT_SOCIAL_WELFARE',
    label: 'Employment on certain social welfare schemes',
  },
  { value: 'EXEMPT_FOR_OTHER_REASON', label: 'Exempt for other reason' },
];

export const defaultASCCalculationMethodOptions = [
  { value: 'NOT_APPLICABLE', label: 'Not applicable' },
  { value: 'STANDARD_ACCRUAL', label: 'Standard Accrual Pension Scheme' },
  { value: 'FACT_ACCRUAL', label: 'Fast Accrual Pension Scheme' },
  { value: 'SINGLE', label: 'Single Pension Scheme' },
];

export const defaultAepnChangeEventOptions = [
  { label: 'Auto Enrolment', value: 'Auto Enrolment' },
  { label: 'Enrolment', value: 'Enrolment' },
  { label: 'Exempt', value: 'Exempt' },
  { label: 'Opt Out', value: 'Opt Out' },
  { label: 'Cessation/Suspend', value: 'Cessation/Suspend' },
  { label: 'Opt In', value: 'Opt In' },
  { label: 'Enrolment post Opt In', value: 'Enrolment post Opt In' },
  {
    label: 'Identification of automatic Re-enrolment',
    value: 'Identification of automatic Re-enrolment',
  },
  { label: 'Re-enrolment completion', value: 'Re-enrolment completion' },
  { label: 'AEL Breach', value: 'AEL Breach' },
  {
    label: 'Next Financial Year post AEL Breach',
    value: 'Next Financial Year post AEL Breach',
  },
  { label: 'Contribution Rate Change 1', value: 'Contribution Rate Change 1' },
  { label: 'Contribution Rate Change 2', value: 'Contribution Rate Change 2' },
  { label: 'Contribution Rate Change 3', value: 'Contribution Rate Change 3' },
  {
    label: 'Pension Status Check Failed',
    value: 'Pension Status Check Failed',
  },
  { label: 'Left Employment', value: 'Left Employment' },
  { label: 'Death', value: 'Death' },
  { label: 'Retirement', value: 'Retirement' },
];

export const defaultAepnEnrolmentStatusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Exempt', value: 'Exempt' },
  { label: 'Opted Out', value: 'Opted Out' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'Opted In', value: 'Opted In' },
  { label: 'To Be Re-enrolled', value: 'To Be Re-enrolled' },
  { label: 'Ineligible', value: 'Ineligible' },
  { label: 'Left Employment', value: 'Left Employment' },
  { label: 'Deceased', value: 'Deceased' },
  { label: 'Retired', value: 'Retired' },
];

export const getTypeOfValueOptions = (currencySymbol = '€') => [
  { label: '%', value: EeErPensionMethodEnum.PERCENTAGE },
  { label: currencySymbol, value: EeErPensionMethodEnum.AMOUNT },
];

export const defaultPaymentTypeSubOptions = [
  { label: 'Fixed', value: 'FIXED' },
  { label: 'Variable', value: 'VARIABLE' },
].sort(compare);

export const defaultRepetitionOptions = [
  {
    label: 'Apply to this period only (One-off)',
    value: 'APPLICABLE_TO_THIS_PERIOD_ONLY',
  },
  {
    label: 'Apply until manually removed (Annual)',
    value: 'REPEAT_UNTIL_MANUALLY_REMOVED',
  },
];

export const defaultExpenseClaimRuleOptions = [
  {
    label: 'Disallow',
    value: 'DISALLOW',
  },
  {
    label: 'Allow',
    value: 'ALLOW',
  },
];

export const defaultVatMethodOptions = [
  { label: 'Standard', value: 'STANDARD' },
  { label: 'Flat Rate VAT', value: 'FLAT_RATE_VAT' },
];

export const defaultHolidayPayCalcOptions = [
  { label: 'NMW Only', value: 'NMW' },
  { label: 'All Earnings', value: 'ALL_EARNINGS' },
];

export const defaultPensionMethodOptions = [
  { label: 'Qualifying Earnings', value: 'QUALIFYING_EARNINGS' },
  { label: 'All Pensionable Earnings', value: 'ALL_PENSIONABLE_EARNINGS' },
];

export const defaultAEPostponementDaysOptions = [
  { label: 'No Postponement', value: 'NO' },
  { label: '3 months', value: '3_MONTHS' },
  { label: '2 months', value: '2_MONTHS' },
  { label: '1 months', value: '1_MONTHS' },
  { label: '4 weeks', value: '4_WEEKS' },
  { label: '3 weeks', value: '3_WEEKS' },
  { label: '2 weeks', value: '2_WEEKS' },
  { label: '1 weeks', value: '1_WEEK' },
];

export const defaultCandidateStatus = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Ongoing', value: 'ONGOING' },
  { label: 'Registration Complete', value: 'REGISTRATION_COMPLETE' },
].sort(compare);

export const defaultAgencyRefMethodOptions = [
  {
    label: 'System Default',
    value: 'SYSTEM_DEFAULT',
  },
  { label: 'NI Number', value: 'NI_NUMBER' },
];

// Ireland has no NI Number — the equivalent identifier is the PPS Number.
// Same stored value, different label (matches the onboarding option relabel).
export const getAgencyRefMethodOptions = (isIreland: boolean) =>
  defaultAgencyRefMethodOptions.map((option) =>
    isIreland && option.value === 'NI_NUMBER'
      ? { ...option, label: 'PPS Number' }
      : option,
  );

export const defaultBankFileReferenceFieldOptions = [
  { label: 'Payslip ID + Surname (Default)', value: 'invoice_id' },
  { label: 'Employee ID', value: 'employee_ref' },
  { label: 'Employee Name', value: 'employee_name' },
  { label: 'Tax Period', value: 'tax_period' },
  { label: 'Employer Ref', value: 'employer_ref' },
];

// The employer_ref option is labelled "Employer Ref" for Payroll (bureau)
// and "Agency Ref" for Umbrella, matching the Employees list column.
export const getBankFileReferenceFieldOptions = (isBureau: boolean) =>
  defaultBankFileReferenceFieldOptions.map((option) =>
    option.value === 'employer_ref'
      ? { ...option, label: isBureau ? 'Employer Ref' : 'Agency Ref' }
      : option,
  );

export const defaultMarginMethodCompany = [
  { label: 'Fixed Fee', value: 'FIXED_FEE' },
  { label: '% on Timesheet Value', value: '%_ON_TIMESHEET_VALUE' },
  { label: 'None', value: 'NONE' },
].sort(compare);

// The reference field is labelled "Employer Ref" for Payroll (bureau)
// and "Agency Ref" for Umbrella, matching the Employees list column.
export const getTimeSheetImportValidationOptions = (isBureau: boolean) => {
  const refLabel = isBureau ? 'Employer Ref' : 'Agency Ref';
  return [
    {
      label: `${refLabel} + Forename & Surname`,
      value: 'AGENCY_REFERENCE_FORENAME_SURNAME',
    },
    { label: refLabel, value: 'AGENCY_REFERENCE' },
    { label: `${refLabel} + Surname`, value: 'AGENCY_REFERENCE_SURNAME' },
  ].sort(compare);
};

export const defaultOnBoardingOptions = [
  {
    label: 'Verify your ID',
    value: 'VERIFY_YOUR_ID',
    description:
      'Nxsys integrates with Yoti for ID verification. ID verification is charged at £1.50 per verification',
  },
  { label: 'Add DOB', value: 'ADD_DOB' },
  {
    label: 'Verify Contract',
    value: 'VERIFY_CONTRACT',
    description: 'Nxsys integrates with Zoho sign for e-signing',
  },
  { label: 'Add Bank Details', value: 'ADD_BANK_DETAILS' },
  { label: 'Add Address Details', value: 'ADD_ADDRESS_DETAILS' },
  { label: 'Add Job Title', value: 'ADD_JOB_DESCRIPTION' },
  // NX-5228: both are UK statutory instruments, so they are country-scoped as
  // well. The Working Time Regulations 1998 let a worker opt out of the 48-hour
  // average week; Ireland's Organisation of Working Time Act 1997 has no such
  // individual opt-out, so Opt-In/Opt-Out has nothing to mean there. The
  // Conduct of Employment Agencies Regulations 2003 have no Irish counterpart
  // at all — and with `isBureau: false` alone this one survived the filter on
  // every Irish umbrella employer, the same way ADD_UTR_NUMBER did.
  {
    label: 'Add Working Time Regulation',
    value: 'ADD_WORKING_TIME_REGULATION',
    isUk: true,
  },
  {
    label: 'Add Agency Conduct Regulation',
    value: 'ADD_AGENCY_CONDUCT_REGULATION',
    isBureau: false,
    isUk: true,
  },
  {
    label: 'Add P45 Starter Declaration',
    value: 'ADD_P45_STARTER_DECLARATION',
    isUk: true,
  },
  { label: 'Add P45 Details', value: 'ADD_P45_DETAILS', isUk: true },
  { label: 'Add NI Number', value: 'ADD_NI_NUMBER' },
  { label: 'Add Holiday Pay Method', value: 'ADD_HOLIDAY_PAY_METHOD' },
  // { label: 'Add OPT In For Pension ', value: 'ADD_OPT_IN_FOR_PENSION' },
  // NX-5228: UTR is a UK tax reference — Ireland's equivalent is the TRN — so
  // this is country-scoped as well as umbrella-only. With `isBureau: false`
  // alone it survived the filter on every Irish umbrella employer.
  {
    label: 'Add UTR Number',
    value: 'ADD_UTR_NUMBER',
    isBureau: false,
    isUk: true,
  },
  {
    label: 'Add Capture ID Docs',
    value: 'ADD_CAPTURE_ID_DOCS',
    isIreland: true,
  },
];

export const defaultUKVerifiedIdMethodOptions = [
  { label: 'Manual Verification', value: 'MANUAL_VERIFICATION' },
  { label: 'Accept Birth Certificate', value: 'ACCEPT_BIRTH_CERTIFICATE' },
];

export const defaultNonUKVerifiedIdMethodOptions = [
  {
    label: 'Manually Verify Share Code / EC Check Code',
    value: 'MANUAL_VERIFICATION',
  },
  // { label: 'Accept Share Code / EC Check Code', value: 'ACCEPT_SHARE_CODE' },
];

// Ireland has no Share Code / EC Check Code scheme, so the Non EU Nationals
// method is a plain manual verification.
export const defaultNonEUVerifiedIdMethodOptions = [
  { label: 'Manual Verification', value: 'MANUAL_VERIFICATION' },
  // { label: 'Accept Share Code / EC Check Code', value: 'ACCEPT_SHARE_CODE' },
];

export const defaultNonUkRightToWorkStatus = [
  { label: 'Mandatory', value: 'MANDATORY' },
  { label: 'Optional', value: 'OPTIONAL' },
].sort(compare);

export const finalSubmitTypeOptions = [
  {
    label: 'For Year',
    value: 'forYear',
  },
  {
    label: 'Scheme Ceased',
    value: 'schemeCeased',
  },
];

export const aepnEnrolmentStatusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Exempt', value: 'Exempt' },
  { label: 'Opted Out', value: 'Opted Out' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'Opted In', value: 'Opted In' },
  { label: 'To Be Re-enrolled', value: 'To Be Re-enrolled' },
  { label: 'Ineligible', value: 'Ineligible' },
  { label: 'Left Employment', value: 'Left Employment' },
  { label: 'Deceased', value: 'Deceased' },
  { label: 'Retired', value: 'Retired' },
];

export const directorshipOptions = [
  { value: 'NONE', label: 'None' },
  { value: 'PROPRIETARY', label: 'Proprietary' },
  { value: 'NON_PROPRIETARY', label: 'Non-Proprietary' },
];
